import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AMinuteWithGodLanguage = () => {
  const { language } = useParams();
  const [audioData, setAudioData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('daily-devotion');
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);

  const languageInfo = {
    kissi: { 
      name: 'Kissi', 
      description: 'For our Kissi-speaking listeners in Sierra Leone, Liberia, and Guinea',
      region: 'Sierra Leone • Liberia • Guinea'
    },
    english: { 
      name: 'English', 
      description: 'For our English-speaking audience across West Africa',
      region: 'West Africa'
    },
    french: { 
      name: 'French', 
      description: 'Pour nos auditeurs francophones en Guinée',
      region: 'Guinea • Francophone Africa'
    },
    mandingo: { 
      name: 'Mandingo', 
      description: 'For Mandingo/Mandinka speakers across the Mano River region',
      region: 'Mano River Region'
    },
    fulani: { 
      name: 'Fulani', 
      description: 'For our Fulani/Fula/Pulaar listeners',
      region: 'Guinea • West Africa'
    },
    gbandi: { 
      name: 'Gbandi', 
      description: 'For Gbandi speakers in Lofa County, Liberia',
      region: 'Lofa County, Liberia'
    }
  };

  const categories = [
    { id: 'daily-devotion', name: 'Daily Devotion', icon: '🙏' },
    { id: 'bible-stories', name: 'Bible Stories', icon: '📖' },
    { id: 'psalms', name: 'Psalms & Prayers', icon: '🎵' },
    { id: 'gospel', name: 'Gospel Messages', icon: '✝️' },
    { id: 'proverbs', name: 'Wisdom & Proverbs', icon: '💡' }
  ];

  const currentLang = languageInfo[language] || languageInfo.english;

  useEffect(() => {
    fetchAudioContent();
  }, [language]);

  const fetchAudioContent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/audio-bible/${language}`);
      if (response.ok) {
        const data = await response.json();
        setAudioData(data);
      } else {
        setAudioData(getStaticAudioData(language));
      }
    } catch (error) {
      console.error('Error fetching audio content:', error);
      setAudioData(getStaticAudioData(language));
    }
    setLoading(false);
  };

  const getStaticAudioData = (lang) => {
    const staticData = {
      kissi: {
        'daily-devotion': [
          { 
            id: 'k1', 
            title: 'Morning Prayer', 
            duration: '3:45',
            description: 'A beautiful morning devotion in Kissi language',
            audioUrl: 'https://globalrecordings.net/en/program/C00681',
            downloadUrl: 'https://globalrecordings.net/en/language/3077',
            source: 'Global Recordings Network',
            verse: 'Psalm 5:3 - In the morning, Lord, you hear my voice'
          },
          { 
            id: 'k2', 
            title: 'Words of Life', 
            duration: '5:20',
            description: 'Evangelistic message in Kissi',
            audioUrl: 'https://globalrecordings.net/en/program/C00681',
            downloadUrl: 'https://globalrecordings.net/en/language/3077',
            source: 'Global Recordings Network',
            verse: 'John 3:16 - For God so loved the world'
          }
        ],
        'bible-stories': [
          { 
            id: 'k3', 
            title: 'Creation Story', 
            duration: '8:30',
            description: 'The story of how God created the world',
            audioUrl: 'https://globalrecordings.net/en/program/C00681',
            downloadUrl: 'https://globalrecordings.net/en/language/3077',
            source: 'Global Recordings Network',
            verse: 'Genesis 1:1 - In the beginning God created the heavens and the earth'
          }
        ],
        'gospel': [
          { 
            id: 'k4', 
            title: 'Good News', 
            duration: '6:15',
            description: 'The Gospel message in Kissi',
            audioUrl: 'https://globalrecordings.net/en/program/C00681',
            downloadUrl: 'https://globalrecordings.net/en/language/3077',
            source: 'Global Recordings Network',
            verse: 'Romans 10:9 - If you declare with your mouth, "Jesus is Lord"'
          }
        ]
      },
      english: {
        'daily-devotion': [
          { 
            id: 'e1', 
            title: 'Morning Praise', 
            duration: '4:00',
            description: 'Start your day praising the Lord',
            audioUrl: 'https://www.bible.com/audio-bible-app',
            downloadUrl: 'https://www.bible.com/versions/1-kjv-king-james-version',
            source: 'Bible.com',
            verse: 'Psalm 118:24 - This is the day the Lord has made'
          },
          { 
            id: 'e2', 
            title: 'Evening Reflection', 
            duration: '5:30',
            description: 'Peaceful reflection for the evening',
            audioUrl: 'https://www.biblegateway.com/audio/',
            downloadUrl: 'https://www.biblegateway.com/',
            source: 'Bible Gateway',
            verse: 'Psalm 4:8 - In peace I will lie down and sleep'
          }
        ],
        'psalms': [
          { 
            id: 'e3', 
            title: 'Psalm 23 - The Lord is My Shepherd', 
            duration: '3:20',
            description: 'The beloved Psalm of comfort',
            audioUrl: 'https://www.bible.com/audio-bible-app',
            downloadUrl: 'https://www.bible.com/bible/1/PSA.23.KJV',
            source: 'Bible.com',
            verse: 'The Lord is my shepherd; I shall not want.'
          },
          { 
            id: 'e4', 
            title: 'Psalm 91 - Protection', 
            duration: '4:45',
            description: "God's promise of protection",
            audioUrl: 'https://www.bible.com/audio-bible-app',
            downloadUrl: 'https://www.bible.com/bible/1/PSA.91.KJV',
            source: 'Bible.com',
            verse: 'He who dwells in the shelter of the Most High'
          }
        ],
        'proverbs': [
          { 
            id: 'e5', 
            title: 'Wisdom for Today', 
            duration: '4:10',
            description: 'Practical wisdom from Proverbs',
            audioUrl: 'https://www.bible.com/audio-bible-app',
            downloadUrl: 'https://www.bible.com/bible/1/PRO.3.KJV',
            source: 'Bible.com',
            verse: 'Proverbs 3:5-6 - Trust in the Lord with all your heart'
          }
        ]
      },
      french: {
        'daily-devotion': [
          { 
            id: 'f1', 
            title: 'Prière du Matin', 
            duration: '4:15',
            description: 'Commencez votre journée avec Dieu',
            audioUrl: 'https://globalrecordings.net/fr/',
            downloadUrl: 'https://globalrecordings.net/fr/',
            source: 'Global Recordings Network',
            verse: 'Psaume 5:3 - Éternel! le matin tu entends ma voix'
          },
          { 
            id: 'f2', 
            title: 'Paroles de Vie', 
            duration: '5:45',
            description: 'Message évangélique en français',
            audioUrl: 'https://www.bible.com/fr',
            downloadUrl: 'https://www.bible.com/fr/versions/93-lsg-louis-segond-1910',
            source: 'Bible.com',
            verse: 'Jean 3:16 - Car Dieu a tant aimé le monde'
          }
        ],
        'gospel': [
          { 
            id: 'f3', 
            title: 'La Bonne Nouvelle', 
            duration: '6:30',
            description: "L'évangile de Jésus-Christ",
            audioUrl: 'https://www.bible.com/fr',
            downloadUrl: 'https://www.bible.com/fr/versions/93-lsg-louis-segond-1910',
            source: 'Bible.com',
            verse: 'Romains 10:9 - Si tu confesses de ta bouche le Seigneur Jésus'
          }
        ]
      },
      mandingo: {
        'daily-devotion': [
          { 
            id: 'm1', 
            title: 'Morning Blessing', 
            duration: '4:30',
            description: 'Start your day blessed in Mandingo',
            audioUrl: 'https://globalrecordings.net/en/language/mnk',
            downloadUrl: 'https://globalrecordings.net/en/language/mnk',
            source: 'Global Recordings Network',
            verse: 'Psalm 118:24 - This is the day the Lord has made'
          }
        ],
        'bible-stories': [
          { 
            id: 'm2', 
            title: 'Look, Listen & Live', 
            duration: '12:00',
            description: 'Bible stories for Mandingo speakers',
            audioUrl: 'https://globalrecordings.net/en/language/mnk',
            downloadUrl: 'https://globalrecordings.net/en/language/mnk',
            source: 'Global Recordings Network',
            verse: 'Genesis 1:1 - In the beginning God created'
          }
        ],
        'gospel': [
          { 
            id: 'm3', 
            title: 'Words of Life', 
            duration: '8:45',
            description: 'The Gospel in Mandingo language',
            audioUrl: 'https://globalrecordings.net/en/language/mnk',
            downloadUrl: 'https://globalrecordings.net/en/language/mnk',
            source: 'Global Recordings Network',
            verse: 'John 14:6 - I am the way, the truth, and the life'
          }
        ]
      },
      fulani: {
        'daily-devotion': [
          { 
            id: 'fu1', 
            title: 'Morning Devotion', 
            duration: '4:20',
            description: 'Morning prayer in Fulani/Pulaar',
            audioUrl: 'https://globalrecordings.net/en/language/fuf',
            downloadUrl: 'https://globalrecordings.net/en/language/fuf',
            source: 'Global Recordings Network',
            verse: 'Psalm 5:3 - In the morning, Lord, you hear my voice'
          }
        ],
        'bible-stories': [
          { 
            id: 'fu2', 
            title: 'Look, Listen & Live', 
            duration: '10:30',
            description: 'Biblical audio-visual program in Fulani',
            audioUrl: 'https://globalrecordings.net/en/language/fuf',
            downloadUrl: 'https://globalrecordings.net/en/language/fuf',
            source: 'Global Recordings Network',
            verse: 'Luke 15:4 - The Parable of the Lost Sheep'
          }
        ],
        'gospel': [
          { 
            id: 'fu3', 
            title: 'Good News', 
            duration: '7:15',
            description: 'The Gospel message in Fulani',
            audioUrl: 'https://globalrecordings.net/en/language/fuf',
            downloadUrl: 'https://globalrecordings.net/en/language/fuf',
            source: 'Global Recordings Network',
            verse: 'Romans 6:23 - For the wages of sin is death, but the gift of God is eternal life'
          }
        ]
      },
      gbandi: {
        'daily-devotion': [
          { 
            id: 'g1', 
            title: 'Morning Prayer', 
            duration: '4:00',
            description: 'Start your day with God in Gbandi',
            audioUrl: 'https://globalrecordings.net/en/language/gbg',
            downloadUrl: 'https://globalrecordings.net/en/language/gbg',
            source: 'Global Recordings Network',
            verse: 'Psalm 63:1 - O God, you are my God; earnestly I seek you'
          }
        ],
        'gospel': [
          { 
            id: 'g2', 
            title: 'Words of Life', 
            duration: '6:45',
            description: 'Evangelistic message in Gbandi',
            audioUrl: 'https://globalrecordings.net/en/language/gbg',
            downloadUrl: 'https://globalrecordings.net/en/language/gbg',
            source: 'Global Recordings Network',
            verse: 'Acts 16:31 - Believe in the Lord Jesus, and you will be saved'
          }
        ]
      }
    };
    return staticData[lang] || staticData.english;
  };

  const currentContent = audioData?.[selectedCategory] || [];

  const handlePlayAudio = (audio) => {
    if (currentAudio?.id === audio.id && isPlaying) {
      setIsPlaying(false);
    } else {
      setCurrentAudio(audio);
      setIsPlaying(true);
    }
  };

  const handleDownload = (audio) => {
    window.open(audio.downloadUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-kioo-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading audio resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-kioo-primary via-kioo-secondary to-kioo-dark text-white py-12 lg:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link 
            to="/a-minute-with-god"
            className="inline-flex items-center text-green-100 hover:text-white mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to All Languages
          </Link>
          
          <div className="text-center">
            <div className="mb-4">
              <span className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm font-medium backdrop-blur-sm">
                ✝️ {currentLang.name} Audio Bible
              </span>
            </div>
            
            <h1 className="text-3xl lg:text-4xl font-bold mb-4">
              A Minute with God
            </h1>
            <p className="text-lg text-green-100 max-w-2xl mx-auto mb-2">
              {currentLang.description}
            </p>
            <p className="text-sm text-green-200">
              📍 {currentLang.region}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Selection */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Choose Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`p-3 rounded-lg text-center transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-kioo-primary text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="text-xl block mb-1">{cat.icon}</span>
                <span className="text-xs font-medium">{cat.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Audio Content */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-kioo-primary to-kioo-secondary p-5">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <span>🎧</span>
              {categories.find(c => c.id === selectedCategory)?.name} in {currentLang.name}
            </h2>
          </div>

          {currentContent.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🙏</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Coming Soon!</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-4">
                We are working on adding more {categories.find(c => c.id === selectedCategory)?.name.toLowerCase()} content 
                in {currentLang.name}. Please try another category.
              </p>
              <a 
                href="https://globalrecordings.net" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block px-6 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors"
              >
                Visit Global Recordings Network →
              </a>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {currentContent.map((audio) => (
                <div 
                  key={audio.id}
                  className={`p-5 hover:bg-gray-50 transition-colors ${
                    currentAudio?.id === audio.id ? 'bg-green-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Play Button */}
                    <button
                      onClick={() => handlePlayAudio(audio)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
                        currentAudio?.id === audio.id && isPlaying
                          ? 'bg-kioo-primary text-white'
                          : 'bg-gray-100 text-kioo-primary hover:bg-kioo-primary hover:text-white'
                      }`}
                    >
                      {currentAudio?.id === audio.id && isPlaying ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      )}
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{audio.title}</h3>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {audio.duration}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{audio.description}</p>
                      
                      {/* Scripture Verse */}
                      <div className="bg-amber-50 border-l-3 border-amber-400 p-3 rounded-r mb-3">
                        <p className="text-sm text-amber-800 italic">"{audio.verse}"</p>
                      </div>

                      {/* Source & Actions */}
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="text-xs text-gray-500">
                          Source: {audio.source}
                        </span>
                        <div className="flex gap-2">
                          <a
                            href={audio.audioUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            🔗 Listen Online
                          </a>
                          <button
                            onClick={() => handleDownload(audio)}
                            className="px-3 py-1.5 text-sm bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors"
                          >
                            ⬇️ Download
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Now Playing Indicator */}
                  {currentAudio?.id === audio.id && isPlaying && (
                    <div className="mt-4 flex items-center gap-3">
                      <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-kioo-primary animate-pulse" style={{width: '45%'}}></div>
                      </div>
                      <span className="text-xs text-kioo-primary font-medium">Now Playing</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Other Languages */}
        <div className="bg-white rounded-xl shadow-md p-6 mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Other Languages</h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {Object.entries(languageInfo).map(([key, lang]) => (
              <Link
                key={key}
                to={`/a-minute-with-god/${key}`}
                className={`p-3 rounded-lg text-center transition-all ${
                  key === language
                    ? 'bg-kioo-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="text-sm font-medium">{lang.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AMinuteWithGodLanguage;
